import { useEffect } from 'react';
import { formatTokenCountForWarning } from '../utils/contextTokenWarning';

interface ContextTokenWarningDialogProps {
  tokenCount: number | null;
  onProceed: () => void;
  onCancel: () => void;
}

export default function ContextTokenWarningDialog({
  tokenCount,
  onCancel,
}: ContextTokenWarningDialogProps) {
  useEffect(() => {
    if (tokenCount === null) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tokenCount, onCancel]);

  if (tokenCount === null) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[215] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onCancel();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="context-token-warning-title"
        className="w-full max-w-md rounded-2xl border border-border bg-background p-6 shadow-2xl"
      >
        <h2
          id="context-token-warning-title"
          className="text-xl font-semibold text-foreground"
          style={{ fontFamily: 'var(--app-heading-font)' }}
        >
          Long conversation warning
        </h2>
        <p className="mt-3 text-sm leading-6 text-foreground/90">
          Your conversation is at {formatTokenCountForWarning(tokenCount)} tokens. Long sessions may reset unexpectedly. You can /new to start fresh.
        </p>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-input bg-secondary px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: 'var(--theme-primary)' }}
          >
            Ignore
          </button>
        </div>
      </div>
    </div>
  );
}
